import React from 'react';
import { Card, useIsMobile } from '../components/ui';
import PageLayout from '../components/layout/PageLayout';

const S = {
  h2: { fontSize: '16px', fontWeight: 700, color: 'var(--gray-900)', marginTop: '26px', marginBottom: '10px' },
  p: { fontSize: '13.5px', color: 'var(--gray-600)', lineHeight: 1.7, marginBottom: '10px' },
  li: { fontSize: '13.5px', color: 'var(--gray-600)', lineHeight: 1.7, marginBottom: '6px' },
};

export default function Privacy() {
  const isMobile = useIsMobile();
  return (
    <PageLayout>
      <div style={{ maxWidth: '760px', margin: '0 auto', padding: isMobile ? '0' : '8px 0' }}>
        <h1 style={{ fontSize: '26px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gray-900)', marginBottom: '4px' }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: '12.5px', color: 'var(--gray-400)', marginBottom: '24px' }}>Last updated: July 2026</p>

        <Card>
          <p style={S.p}>
            HopeForSomeone connects blood donors, recipients, and organizations. This page explains exactly
            what we collect, why, and what we do — and don't do — with it. In an emergency, always contact
            local emergency services first; this platform coordinates donors, it does not provide medical care.
          </p>

          <div style={S.h2}>What we collect</div>
          <p style={S.p}>When you create an account, we collect your name, email, phone number, and password
            (which we never store in plain text — it's hashed with bcrypt before it ever touches our database).</p>
          <ul style={{ paddingLeft: '20px', marginBottom: '10px' }}>
            <li style={S.li}><strong>Donors:</strong> blood type, date of birth, gender, weight, and your answers to
              our health screening questions — this determines donation eligibility.</li>
            <li style={S.li}><strong>Location (optional):</strong> city, state, or precise GPS coordinates if you
              enable it, used to match you to nearby requests. You can leave this blank.</li>
            <li style={S.li}><strong>Organizations:</strong> organization name, type, registration number, and address.</li>
            <li style={S.li}><strong>Blood requests:</strong> hospital name, patient name (optional), urgency, and any
              notes you add.</li>
          </ul>
          <p style={S.p}>We do not use tracking cookies, behavioral advertising, or third-party analytics scripts.
            The only thing stored in your browser is your login session.</p>

          <div style={S.h2}>How we use it</div>
          <ul style={{ paddingLeft: '20px', marginBottom: '10px' }}>
            <li style={S.li}>Matching your blood type and location against open requests, and alerting you by SMS
              and email when there's a match.</li>
            <li style={S.li}>Sharing your name and phone number with a requester — <strong>only</strong> if you
              explicitly accept their request. We never share your contact info before that.</li>
            <li style={S.li}>Verifying your email and phone number before you can post or respond to a request,
              so contact information on the platform is real.</li>
            <li style={S.li}>Verifying organizations before they can list donation camps.</li>
          </ul>

          <div style={S.h2}>Who else sees it</div>
          <p style={S.p}>We send SMS through Twilio and email through our SMTP provider — your phone number,
            email address, and message content pass through their infrastructure to reach you. We do not sell
            or rent your data to advertisers, and the sponsored content shown on this platform is not targeted
            using your personal data.</p>

          <div style={S.h2}>How it's protected</div>
          <ul style={{ paddingLeft: '20px', marginBottom: '10px' }}>
            <li style={S.li}>Passwords are hashed, never stored or logged in plain text.</li>
            <li style={S.li}>Sessions use short-lived access tokens with a separate refresh token, not a
              permanent login credential sitting in your browser.</li>
            <li style={S.li}>Sensitive actions (posting a request, accepting one) are rate-limited and require
              a verified email and phone.</li>
            <li style={S.li}>Organizations are reviewed by an admin before they can list camps.</li>
          </ul>

          <div style={S.h2}>Your data, your choice</div>
          <p style={S.p}>
            We don't yet have a self-service export/delete button — until we do, email{' '}
            <a href="mailto:hello@hopeforsomeone.com" style={{ color: 'var(--red)', fontWeight: 600 }}>
              hello@hopeforsomeone.com
            </a>{' '}
            and we'll access, correct, or delete your data on request. We keep your information for as long as
            your account is active, or until you ask us to remove it.
          </p>

          <div style={S.h2}>Location data sources</div>
          <p style={S.p}>
            Our country, state and city list — including the coordinates that let us match you to nearby
            requests — comes from the{' '}
            <a
              href="https://github.com/dr5hn/countries-states-cities-database"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--red)', fontWeight: 600 }}
            >
              Countries States Cities Database
            </a>{' '}
            (licensed under the{' '}
            <a
              href="https://opendatacommons.org/licenses/odbl/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--red)', fontWeight: 600 }}
            >
              ODbL
            </a>
            ), which is derived from GeoNames. When we turn a street address or a GPS reading into a place
            name, we use{' '}
            <a
              href="https://www.openstreetmap.org/copyright"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--red)', fontWeight: 600 }}
            >
              OpenStreetMap
            </a>{' '}
            via Nominatim — that lookup sends the address or coordinates to their servers, and nothing else
            about you.
          </p>

          <div style={S.h2}>Questions</div>
          <p style={S.p}>
            Reach us at <a href="mailto:hello@hopeforsomeone.com" style={{ color: 'var(--red)', fontWeight: 600 }}>hello@hopeforsomeone.com</a> with
            anything about this policy or your data.
          </p>
        </Card>
      </div>
    </PageLayout>
  );
}
